import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { notificationsApi } from '../api';
import { subscribeToNotifications } from '../api/websocket';
import HrnbContainer from '../components/controls/HrnbContainer';
import HrnbButton from '../components/controls/HrnbButton';
import HrnbAlert from '../components/controls/HrnbAlert';
import HrnbModal from '../components/controls/HrnbModal';
import { SkeletonList } from '../components/controls/HrnbSkeleton';
import { AcceptIcon, DeleteIcon, BackIcon, ForwardIcon, ClearIcon } from '../icons';
import '../css/pages/NotificationsPage.css';

const PAGE_SIZE = 20;

const NotificationsPage = () => {
    const { currentUser } = useContext(UserContext);
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [filter, setFilter] = useState('all'); // all | unread
    const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);

    const loadNotifications = useCallback(async (pageNum = 0) => {
        if (!currentUser) return;
        setLoading(true);
        setError(null);
        try {
            const params = { page: pageNum, size: PAGE_SIZE, sortBy: 'createdAt', direction: 'DESC' };
            const data = await notificationsApi.list(params);
            setNotifications(data.content || []);
            setTotalPages(data.totalPages || 0);
            setPage(pageNum);
        } catch (err) {
            console.error('[NotificationsPage] Failed to load notifications:', err.message);
            setError('Не удалось загрузить уведомления');
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        loadNotifications(0);
    }, [loadNotifications]);

    // WebSocket подписка — auto-refresh при новых уведомлениях
    useEffect(() => {
        if (!currentUser) return;

        const wsSubscription = subscribeToNotifications((notification) => {
            console.log('[NotificationsPage] Новое уведомление:', notification.type, notification.title);
            // Перезагружаем список, если страница активна
            loadNotifications(page);
        });

        return () => {
            if (wsSubscription) {
                wsSubscription.unsubscribe();
            }
        };
    }, [currentUser, page, loadNotifications]);

    const handleMarkAsRead = async (id) => {
        try {
            await notificationsApi.markAsRead(id);
            setNotifications(prev =>
                prev.map(n => (n.id === id ? { ...n, isRead: true } : n))
            );
        } catch (err) {
            console.error('[NotificationsPage] Failed to mark as read:', err.message);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await notificationsApi.markAllAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        } catch (err) {
            console.error('[NotificationsPage] Failed to mark all as read:', err.message);
        }
    };

    const handleDelete = async (id) => {
        try {
            await notificationsApi.remove(id);
            setNotifications(prev => prev.filter(n => n.id !== id));
        } catch (err) {
            console.error('[NotificationsPage] Failed to delete notification:', err.message);
        }
    };

    const handleDeleteAll = async () => {
        try {
            // Удаляем по одному, так как сервер может не поддерживать bulk delete
            await Promise.all(notifications.map(n => notificationsApi.remove(n.id)));
            setNotifications([]);
            setShowDeleteAllModal(false);
            navigate('/cabinet');
        } catch (err) {
            console.error('[NotificationsPage] Failed to delete all notifications:', err.message);
            setError('Не удалось удалить все уведомления');
        }
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getNotificationIcon = (type) => {
        const icons = {
            NEW_COMMENT: '💬',
            NEW_MESSAGE: '📩',
            BATTLE_STARTED: '⚔️',
            ROUND_STARTED: '🎯',
            APPLICATION_APPROVED: '✅',
            APPLICATION_REJECTED: '❌',
            NEW_FOLLOWER: '👤',
            VOTING_STARTED: '🗳️',
            VOTING_ENDED: '🏆',
        };
        return icons[type] || '🔔';
    };

    const getNotificationLink = (notification) => {
        const payload = notification.payload || {};
        switch (notification.type) {
            case 'NEW_COMMENT':
                return payload.postId ? `/forum/posts/${payload.postId}` : '/forum';
            case 'NEW_MESSAGE':
                return payload.chatId ? `/chats/${payload.chatId}` : '/chats';
            case 'BATTLE_STARTED':
            case 'ROUND_STARTED':
                return payload.battleId ? `/battles/${payload.battleId}` : '/battles';
            case 'APPLICATION_APPROVED':
            case 'APPLICATION_REJECTED':
                return payload.battleId ? `/battles/${payload.battleId}/participants` : '/battles';
            case 'NEW_FOLLOWER':
                return payload.userId ? `/profile/${payload.userId}` : '/profiles';
            default:
                return '#';
        }
    };

    const filteredNotifications = filter === 'unread'
        ? notifications.filter(n => !n.isRead)
        : notifications;

    const hasUnread = notifications.some(n => !n.isRead);

    if (!currentUser) {
        return (
            <HrnbContainer className="notifications-page">
                <div className="notifications-page__empty">
                    <p>Войдите в систему, чтобы просматривать уведомления</p>
                </div>
            </HrnbContainer>
        );
    }

    return (
        <HrnbContainer className="notifications-page">
            <div className="notifications-page__header">
                <h1 className="notifications-page__title">Уведомления</h1>
                <div className="notifications-page__actions">
                    <div className="notifications-page__filter">
                        <button
                            className={`notifications-page__filter-btn ${filter === 'all' ? 'active' : ''}`}
                            onClick={() => setFilter('all')}
                        >
                            Все
                        </button>
                        <button
                            className={`notifications-page__filter-btn ${filter === 'unread' ? 'active' : ''}`}
                            onClick={() => setFilter('unread')}
                        >
                            Непрочитанные
                        </button>
                    </div>
                    {hasUnread && (
                        <HrnbButton variant="outline" size="sm" onClick={handleMarkAllAsRead} icon={<AcceptIcon />}>
                            Отметить все как прочитанные
                        </HrnbButton>
                    )}
                    {notifications.length > 0 && (
                        <HrnbButton variant="danger" size="sm" onClick={() => setShowDeleteAllModal(true)} icon={<DeleteIcon />}>
                            Удалить все
                        </HrnbButton>
                    )}
                </div>
            </div>

            {error && <HrnbAlert variant="danger" message={error} />}

            {loading ? (
                <SkeletonList count={5} />
            ) : filteredNotifications.length === 0 ? (
                <div className="notifications-page__empty">
                    <span className="notifications-page__empty-icon">🔔</span>
                    <p>Нет уведомлений</p>
                </div>
            ) : (
                <div className="notifications-page__list">
                    {filteredNotifications.map(notification => (
                        <div
                            key={notification.id}
                            className={`notification-item ${!notification.isRead ? 'notification-item--unread' : ''}`}
                        >
                            <a
                                href={getNotificationLink(notification)}
                                className="notification-item__link"
                                onClick={() => handleMarkAsRead(notification.id)}
                            >
                                <span className="notification-item__icon">
                                    {getNotificationIcon(notification.type)}
                                </span>
                                <div className="notification-item__content">
                                    <h3 className="notification-item__title">{notification.title}</h3>
                                    <p className="notification-item__body">{notification.body}</p>
                                    <span className="notification-item__time">
                                        {formatTime(notification.createdAt)}
                                    </span>
                                </div>
                            </a>
                            <div className="notification-item__actions">
                                {!notification.isRead && (
                                    <button
                                        className="notification-item__action"
                                        onClick={() => handleMarkAsRead(notification.id)}
                                        title="Отметить как прочитанное"
                                    >
                                        ✓
                                    </button>
                                )}
                                <button
                                    className="notification-item__action notification-item__action--delete"
                                    onClick={() => handleDelete(notification.id)}
                                    title="Удалить"
                                >
                                    🗑️
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {totalPages > 1 && (
                <div className="notifications-page__pagination">
                    <HrnbButton
                        variant="outline"
                        size="sm"
                        disabled={page === 0}
                        onClick={() => loadNotifications(page - 1)}
                        icon={<BackIcon />}
                    >
                        Назад
                    </HrnbButton>
                    <span className="notifications-page__page-info">
                        Страница {page + 1} из {totalPages}
                    </span>
                    <HrnbButton
                        variant="outline"
                        size="sm"
                        disabled={page >= totalPages - 1}
                        onClick={() => loadNotifications(page + 1)}
                        icon={<ForwardIcon />}
                    >
                        Вперёд
                    </HrnbButton>
                </div>
            )}

            <HrnbModal
                open={showDeleteAllModal}
                onClose={() => setShowDeleteAllModal(false)}
                title="Подтверждение удаления"
            >
                <p>Вы уверены, что хотите удалить все уведомления?</p>
                <div style={{ display: 'flex', gap: '12px', marginTop: '20px', justifyContent: 'flex-end' }}>
                    <HrnbButton variant="secondary" size="sm" onClick={() => setShowDeleteAllModal(false)} icon={<ClearIcon />}>
                        Отмена
                    </HrnbButton>
                    <HrnbButton variant="danger" size="sm" onClick={handleDeleteAll} icon={<DeleteIcon />}>
                        Удалить все
                    </HrnbButton>
                </div>
            </HrnbModal>
        </HrnbContainer>
    );
};

export default NotificationsPage;
